import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { connect, ConnectedProps } from 'react-redux';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Button, Typography } from '@mui/material';
import { Col, Row } from 'react-bootstrap';
import { useSnackbar } from 'notistack';

import clsx from 'clsx';
import Preloader from '../../../components/ui/Preloader/Preloader';

import { IAppState } from '../../../store/rootDuck';
import { actions as categoriesActions } from '../../../store/ducks/categories.duck';
import { actions as productCatalogActions } from '../../../store/ducks/product-catalog.duck';
import { actions as productActions } from '../../../store/ducks/product.duck';
import { setLayoutSubheader } from '../../../utils/layout';
import { actions as productTypesActions } from '../../../store/ducks/productType.duck';
import { actions as companiesActions } from '../../../store/ducks/companies.duck';
import { actions as profileActions } from '../../../store/ducks/profile.duck';
import { ICompany } from '../../../interfaces/companies';
import { UserRoles } from '../../../interfaces/user';
import { actions as cartActions } from '../../../store/ducks/cart.duck';
import useCrudSnackbar from '../../../hooks/useCrudSnackbar';
import { useStylesProductListById } from './hooks/useStyles';
import FilterCategoriesTree from './components/FilterCategoriesTree';
import ProductsTableListComponent from './components/ProductTable/ProductsTableList';
import { ProductsGrid } from './components';
import AlertCompanyDetail from './components/AlertCompanyDetail';

const ProductList: React.FC<TPropsFromRedux> = ({
  viewType,
  isAdmin,
  isVender,
  filter,
  fetchCatalogCategories,
  catalogCategories,
  setSearch,
  isSearching,
  products,
  loading,
  fetch,
  page,
  perPage,
  clearDel,
  deleteError,
  setEmpty,
  me,
  fetchMe,
  loadingMe,
  successMe,
  clearMe,
  clearProducts,
  addProductSuccess,
  addProductError,
  fetchCompany,
  company,
  clearFetchById,
  clearParameters,
  isAuthorized,
}) => {
  const classes1 = useStylesProductListById({ isAdmin });
  const { classes } = classes1;
  const intl = useIntl();
  const navigate = useNavigate();
  const locationHistory: any = useLocation();
  const { categoryId, companyId } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const [userCompany, setUserCompany] = useState<null | ICompany>();
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  const [isOpenFileDialog, setOpenFileDialog] = useState(false);

  const catId = /U\d+/g.test(categoryId!!) ? categoryId!!.slice(1) : categoryId;

  useEffect(() => {
    fetchMe();
    clearParameters();
    fetchCatalogCategories({ with_products: true, company_id: +companyId!! });
    return () => {
      clearMe();
      clearProducts();
      clearFetchById();
    };
  }, []);

  useEffect(() => {
    if (me && !userCompany) {
      setUserCompany(me.company);
    }
  }, [successMe]);
  useEffect(() => {
    if (locationHistory?.state?.categoryId) {
      setSelectedCategoryId(locationHistory.state.categoryId);
    }
  }, [locationHistory]);

  useEffect(() => {
    if (categoryId && /\d+/g.test(categoryId) && me && !companyId) {
      const companyId = me.company ? me.company.id : undefined;
      fetch({ page, perPage, categoryId: catId ? +catId : undefined, companyId });
    } else {
      setEmpty();
    }
  }, [fetch, categoryId, me]);

  useEffect(() => {
    if (companyId && categoryId && /U\d+/g.test(categoryId)) {
      fetch({ page, perPage, companyId: +companyId });
      fetchCompany(+companyId);
    } else {
      setEmpty();
    }
  }, [fetch, me, companyId]);

  useEffect(() => {
    if (deleteError) {
      enqueueSnackbar(deleteError, { variant: 'error' });
      clearDel();
    }
    return () => {
      clearDel();
    };
  }, [deleteError, clearDel, enqueueSnackbar]);

  useCrudSnackbar({
    success: addProductSuccess,
    error: addProductError,
    successMessage: intl.formatMessage({ id: 'CART.ADD_PRODUCT.SUCCESS' }),
    errorMessage: `${intl.formatMessage({ id: 'ERROR' })}: ${addProductError}`,
  });

  setLayoutSubheader({
    title: intl.formatMessage({ id: 'CATEGORIES.VIEW' }),
    back: true,
  });

  const resetProducts = useCallback(() => {
    if (isAdmin || isVender) {
      fetch({ page, perPage, companyId: +companyId!! });
    }
    fetch({ page, perPage, companyId: +companyId!!, active: true });
  }, []);

  const goToCreateProduct = useCallback(() => {
    if (companyId && company) {
      navigate(`/products/create/company/${company.id}/${company.name}`, {
        state: { catId },
      });
    } else if (me?.is_admin) {
      navigate(`/products/create/${catId}`);
    } else {
      navigate(`/products/create/company/${me?.company?.id}/${me?.company?.name}`, {
        state: { catId },
      });
    }
  }, [navigate, me, categoryId, companyId, company]);

  if (loadingMe || !categoryId) return <Preloader />;

  return (
    <>
      <AlertCompanyDetail
        open={isOpenFileDialog}
        message={intl.formatMessage({ id: 'FILE.IMPORT.ALERT.TEXT' })}
        company={company}
        handleClose={() => {
          setOpenFileDialog(false);
        }}
      />

      {((!me?.roles.includes(UserRoles.ROLE_BUYER) &&
        !me?.roles.includes(UserRoles.ROLE_BUYER_STAFF) &&
        me?.company &&
        me.company.name) ||
        me?.is_admin) && (
        <Button
          style={{ marginBottom: 20 }}
          color='primary'
          variant='contained'
          onClick={() => goToCreateProduct()}
        >
          {intl.formatMessage({ id: 'PRODUCT.BUTTON.ADD' })}
        </Button>
      )}

      <Row>
        <Col>
          <div className={classes.container}>
            {isAdmin && (
              <div className={classes.filterCol}>
                <FilterCategoriesTree
                  categories={catalogCategories || []}
                  resetProducts={resetProducts}
                  searchProducts={fetch}
                  filter={filter}
                  setSearch={setSearch}
                  loading={loading}
                  selectedCategoryId={selectedCategoryId}
                  setSelectedCategoryId={setSelectedCategoryId}
                  companyId={Number(companyId)}
                  isAdmin={isAdmin}
                  isVender={isVender}
                />
              </div>
            )}

            <div className={classes.productsCol}>
              {loading ? (
                <Preloader />
              ) : (
                <>
                  {!products || !products.length ? (
                    <Typography className={classes.empty} component='h5' variant='h5'>
                      {intl.formatMessage({ id: 'PRODUCT.CATALOG.LIST.EMPTY' })}
                    </Typography>
                  ) : (
                    <div className={clsx(classes.card, { [classes.revers]: isSearching })}>
                      <div>
                        <div className={classes.productColHeader}>
                          <div className={classes.productColHeaderItem}>
                            <Typography variant='h6' className={classes.count}>
                              {isSearching
                                ? `${intl.formatMessage({ id: 'ITEMS.FOUND' })} ${
                                    products.length
                                  } `
                                : intl.formatMessage({ id: 'PRODUCT.ALL.PRODUCTS' })}
                            </Typography>
                          </div>
                        </div>

                        {viewType === 'table' ? (
                          <ProductsTableListComponent
                            data={products}
                            isAuthorized={isAuthorized}
                            me={me}
                          />
                        ) : (
                          <ProductsGrid me={me} data={products} />
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </Col>
      </Row>
    </>
  );
};

const connector = connect(
  (state: IAppState) => ({
    products: state.products.products,
    loading: state.products.loading,
    page: state.products.page,
    perPage: state.products.per_page,
    total: state.products.total,
    deleteError: state.products.delError,

    me: state.profile.me,
    loadingMe: state.profile.loading,
    successMe: state.profile.success,

    addProductLoading: state.cart.addProductLoading,
    addProductSuccess: state.cart.addProductSuccess,
    addProductError: state.cart.addProductError,

    isSearching: state.productsCatalog.isSearching,
    productsCount: state.productsCatalog.total,
    filter: state.productsCatalog.filter,
    isAdmin: state.auth.user?.is_admin,
    isVender: state.auth.user?.is_vendor,
    viewType: state.productsCatalog.viewType,

    catalogCategories: state.categories.catalogCategories,

    cartCompanyId: state.cart.companyId,
    company: state.companies.company,
    isAuthorized: state.auth.user != null,
    companyGuestCart: state.cart.guestCart?.cartCompanyId,
  }),
  {
    fetch: productActions.fetchRequest,
    delProduct: productActions.delRequest,
    clearDel: productActions.clearDel,
    setEmpty: productActions.setEmpty,
    clearProducts: productActions.clearProducts,
    clearParameters: productTypesActions.clearParams,

    fetchMe: profileActions.fetchRequest,
    clearMe: profileActions.clearMe,

    fetchCompany: companiesActions.fetchByIdRequest,
    clearFetchById: companiesActions.clearFetchById,

    addProductToCart: cartActions.addProductRequest,
    setProductGuestCart: cartActions.setProductGuestCart,

    setSearch: productCatalogActions.setSearch,
    fetchCatalogCategories: categoriesActions.fetchCatalogCategories,
    fetchPro: productCatalogActions.fetchRequest,
    setViewType: productCatalogActions.setViewType,
  }
);

type TPropsFromRedux = ConnectedProps<typeof connector>;

export default connector(ProductList);
